export interface LambdaResultAction {
  action: string;
  id: string;
}
export interface LambdaResult {
  code: number;
  message: string | LambdaResultAction | (string | LambdaResultAction)[];
}
export interface LambdaResponse {
  executionId: string;
  res: LambdaResult;
}

// A lambda's refusal. A coded error (the lambdas' ErrorResult, for example PUBLIC_LEDGER_SETUP_INCOMPLETE) keeps its code,
// which Buttress sends back as errDetails.code, so a caller can choose what to show; the message is the lambda's own.
export class LambdaError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = 'LambdaError';
    this.code = code;
  }
}

/** The string code in a failed lambda response body (`errDetails.code`), or null when it has none. */
export function errorCodeFromResponse(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const details = (data as { errDetails?: unknown }).errDetails;
  if (typeof details !== 'object' || details === null) return null;
  const code = (details as { code?: unknown }).code;
  return typeof code === 'string' && code ? code : null;
}

/**
 * A refusal a lambda answers in its result rather than as a failed request: job/complete catches its coded ErrorResult and
 * sets `{code: <its string code>, message}`, which Buttress sends back with HTTP 200. A LambdaError keeping that code (so
 * the card can show the public ledger refusal it names), or null for any other result.
 */
export function codedResultError(res: { code?: unknown; message?: unknown } | null | undefined): LambdaError | null {
  if (!res || typeof res.code !== 'string' || !res.code) return null;
  return new LambdaError(typeof res.message === 'string' && res.message ? res.message : res.code, res.code);
}

/** The code a caught lambda error carries, or null for any other error. */
export function lambdaErrorCode(err: unknown): string | null {
  return err instanceof LambdaError ? err.code : null;
}

/**
 * Whether a failed lambda call was cut off before the lambda answered, rather than refused by it: the run was stopped at
 * Buttress's lambda timeout, a gateway answered in its place (no JSON body), or the request never reached an answer. The
 * lambda may have done part of its work, so it may be worth trying again.
 */
export function isInterruptedLambdaCall(err: unknown): boolean {
  // fetch rejects with a TypeError when the connection fails ("Failed to fetch", "NetworkError ...", "Load failed")
  if (err instanceof TypeError) return /fetch|network|load failed/i.test(err.message);
  if (!(err instanceof LambdaError) || err.code) return false;
  return err.message.includes('lambda_execution_timed_out') || err.message.startsWith('Non-200 from lambda');
}

export async function callLambda(
  path: string,
  method: string,
  token?: string,
  body?: BodyInit | undefined,
  query?: string,
): Promise<LambdaResponse> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = new URL(`%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/${path}`);

  const queryString = query ? new URLSearchParams(query).toString() : '';
  url.search = queryString;

  const options = {
    method,
    headers,
    body,
  };

  if (method === 'GET' && body) throw new Error('GET request should not have a body');

  const res = await fetch(url, options);
  const contentType = res.headers.get('content-type');
  let data = null;
  let errMsg = `Non-200 from lambda ${path} - ${res.statusText}`;
  if (contentType && contentType.includes('application/json')) {
    data = await res.json();
    errMsg = data && data.err ? data.err : errMsg;
  }
  if (!res.ok) throw new LambdaError(errMsg, errorCodeFromResponse(data));
  if (!data) throw new Error(`No response data from lambda ${path}`);

  if (!data.executionId) throw new Error('No executionId in lambda response');
  if (!data.res) throw new Error('No res in lambda response');

  return data as LambdaResponse;
}

export async function getActionFromLambdaResults(results: LambdaResponse, search: string) {
  if (results.res.code !== 200) throw new Error('Got non-200 result from lambda');
  if (!results.res.message) throw new Error('Lambda returned no results');
  if (!Array.isArray(results.res.message)) throw new Error('Lambdas are now expected to return an array of actions');
  if (results.res.message.length === 0) throw new Error('Lambda returned no actions');

  const find = results.res.message.find((a) => (typeof a === 'string' ? false : a.action === search));
  if (!find) throw new Error(`Unable to find ${search} action in lambda results`);

  return find;
}

export interface PersonClaims {
  jobTitle?: string;
  director?: boolean;
  paymentApprover?: boolean;
  reportsTo?: { id: string; primary: boolean }[];
}

export async function updatePersonClaims(token: string | undefined, personId: string, claims: PersonClaims) {
  const result = await callLambda('person/update-claims', 'POST', token, JSON.stringify({ personId, ...claims }));
  if (result.res.code !== 200) {
    throw new Error(typeof result.res.message === 'string' ? result.res.message : 'Unable to update your profile');
  }
}
